import { isPlatformBrowser } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Inject,
  Optional,
  PLATFORM_ID,
  RESPONSE_INIT,
  signal,
  TransferState,
  viewChild,
  WritableSignal
} from '@angular/core';
import { Response } from 'express';
import { RESPONSE } from '../../express.tokens';
import { ActivatedRoute } from '@angular/router';
import {
  AttributeSelectionsInterface,
  ConfigurableProduct,
  DistinctProductInterfaceType,
  GeaProduct,
  getUrlHashArguments,
  MatrixProduct,
  PictureInterface,
  PriceViewInterface,
  Product,
  ProductPageInterface,
  ProductVariantInterface,
  SimpleProduct,
  WagtailPageResponseInterface
} from "@gea-waldviertler-website/gea-models";
import { CartService } from "../services/cart.service";
import {
  BreadcrumbService,
  EnvService,
  PageLoadService,
  PageService,
  SEOService
} from "@gea-waldviertler-website/shop-lib";
import { ProductService } from "../services/product.service";
import { Title } from "@angular/platform-browser";
import { Subject } from "rxjs";
import { resolveCategoryErrorStatus, scrollToFirstEl, shouldRenderPageError } from "../helpers";
import { ProductCarouselContentInterface } from "./slider/slider.component";
import { ScreenSizeService } from "../services/screen-size.service";
import { AddToCartDataInterface } from "../models/cart";
import { SSR_API_UNAVAILABLE_KEY } from "../services/http/interceptors/response503.interceptor";

@Component({
    selector: 'gea-shop-product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.scss'],
    standalone: false
})
export class ProductComponent {
  private transferState = inject(TransferState)
  private wasRenderedAsServerError = this.transferState.get(SSR_API_UNAVAILABLE_KEY, false)

  imagesColumn = viewChild("imagesColumn", { read: ElementRef })
  cmsPage?: WagtailPageResponseInterface
  selectedVariant = signal<number>(0)
  pictures: WritableSignal<PictureInterface[]> = signal([])
  distinctProductSignal: WritableSignal<DistinctProductInterfaceType | undefined> = signal(undefined)
  distinctProduct: DistinctProductInterfaceType | undefined
  notfound = signal<boolean>(this.wasRenderedAsServerError)
  errorStatus = signal<404 | 503>(this.wasRenderedAsServerError ? 503 : 404)
  resetSelectionSubject: Subject<boolean> = new Subject<boolean>()
  triggerValidationSubject: Subject<boolean> = new Subject<boolean>()
  // Fired whenever the product page is refreshed for the SAME product (e.g. an
  // active Aktion arriving asynchronously re-emits the product page). Each
  // configurator subscribes and re-emits its currently resolved distinct
  // product so `distinctProduct` is never left blank and the Aktion
  // price/badge refresh while the order tiles / add-to-cart button stay visible.
  distinctRefreshSubject: Subject<void> = new Subject<void>()
  activeImageIndex = signal<number>(0)
  loading: boolean = true
  screenSizeService = inject(ScreenSizeService)

  carouselContent = computed(() => {
    const pc = this.productPageContent()
      if (pc) {
        return {
          collection: pc.product.linkedProducts?.products.productList,
          attributes: pc.product.linkedProducts?.products.productAttributes
        } as ProductCarouselContentInterface
      }
      return undefined
  })

  currentSelections: WritableSignal<AttributeSelectionsInterface | undefined> = signal(undefined)
  storePickupEnabled: boolean = true

  productPageContent: WritableSignal<ProductPageInterface | null | undefined> = signal(undefined)

  configuratorPreselection: string[][] = []

  // identity of the currently loaded product, used to distinguish a genuine
  // product change from a shop-event re-emit of the same product
  private loadedProductIdentity: number | undefined = undefined

  // TODO: Fixme
  readonly ID_SCHUHSPENDE: number = 4710
  readonly ID_SCHUHSPENDE_VARIABEL: number = 17307

  // TODO: https://gitlab.gea.at/gea-waldviertler-website/angular-webshop/-/issues/633
  productHasTax = computed(() => {
    const product = this.productPageContent()?.product
    if (! product) return false
    if (["schuhspende", "akademiegutschein", "geschenkgutschein"].includes(product.slug)) return false

    let hastax = false
    product.taxes.forEach(tax => {
      if (tax > 0) hastax = true
    })
    return hastax
  })

  // TODO: https://gitlab.gea.at/gea-waldviertler-website/angular-webshop/-/issues/626
  productHasDeliveryCosts = computed(() => {
    const product = this.productPageContent()?.product
    if (! product) return false
    const selectedVariant = this.selectedVariant()
    if (! selectedVariant) {
      return ![ "schuhspende", "akademiegutschein", "geschenkgutschein" ].includes(product.slug)
    }
    return (selectedVariant && ! product.getVariant(selectedVariant).freeDelivery)
  })

  private envService = inject(EnvService)

  public forcePrice = signal<number | PriceViewInterface | undefined>(undefined)


  constructor(
      private route: ActivatedRoute,
      private titleService: Title,
      private productService: ProductService,
      @Inject(PLATFORM_ID) private platformId: Object,
      @Inject(CartService) private cart: CartService,
      @Inject(PageLoadService) private pageloadService: PageLoadService,
      @Inject(PageService) private pageService: PageService,
      @Inject(SEOService) private seoService: SEOService,
      @Inject(BreadcrumbService) private bcService: BreadcrumbService,
      @Optional() @Inject(RESPONSE) private response?: Response,
      @Optional() @Inject(RESPONSE_INIT) private responseInit?: ResponseInit | null,
  ) {
    this.pageloadService.getStatusSubject().subscribe(loading => {
      this.loading = loading
    })

    this.pageService.getConfigSubject().subscribe(config => {
      if (config !== undefined) this.storePickupEnabled = config.storePickupEnabled
    })

    // effect should not be used, but as long as gea-waldviertler-website/angular-webshop#612 ist not fully
    // implemented, we need this
    effect(() => {
      let pp = this.productService.getProductPageSignal()()
      if (pp) {
        this.onProductPageLoad(pp)
      }
    })

    this.route.params.subscribe(
      params => {
        let pp = this.productService.getProductPageSignal()()
        if (! pp || (pp && (pp.product.slug !== params['productSlug']))) {
          this.productService.loadProductPage(params['productSlug']).subscribe(responseCode => {
            if (responseCode !== 200) {
              this.pageloadService.getStatusSubject().next(false)
              const status = resolveCategoryErrorStatus(responseCode)
              const browser = isPlatformBrowser(this.platformId)
              this.errorStatus.set(status)
              this.notfound.set(shouldRenderPageError(status, browser, this.wasRenderedAsServerError))
              if (browser && this.wasRenderedAsServerError) {
                this.transferState.remove(SSR_API_UNAVAILABLE_KEY)
                this.wasRenderedAsServerError = false
              }
              if (! browser) {
                this.response?.status(status)
                if (this.responseInit) {
                  this.responseInit.status = status
                }
              }
            }
          })
        }
      }
    )

    effect(() => {
      let ref = this.imagesColumn()
      let ic = ref?.nativeElement.querySelector('.images-column-scroller')
      if (! ic) return
      ic.addEventListener('scroll', (e: Event) => {
        this.onScrollImages((<HTMLDivElement>e.currentTarget).scrollLeft)
      })
      ic.addEventListener('touchend', (e: Event) => {
        this.onScrollImages((<HTMLDivElement>e.currentTarget).scrollLeft)
      })
    })
  }

  isBrowser() {
    // on ssr
    if (! isPlatformBrowser(this.platformId)) return false
    // else check size and return false on mobile and true on desktop
    return this.screenSizeService.currentDimensionIsLargerThan('sm')
  }

  onScrollImages(pixels: number) {
    const ic = this.imagesColumn()
    if (! ic) return
    let itemWidth = ic.nativeElement.clientWidth ?? 0
    if (pixels == 0) this.activeImageIndex.set(0)
    else this.activeImageIndex.set(Math.ceil(pixels / ( itemWidth + 150)))
  }

  navigateToImage(index: number) {
    const ic = this.imagesColumn()
    if (! ic) return
    const itemWidth = ic.nativeElement.clientWidth ?? 0
    ic.nativeElement.querySelector('.images-column-scroller')!.scrollLeft = index * ( itemWidth + 150 )
  }

  updateMetaTags(p: GeaProduct) {
    // only update Title
    if (isPlatformBrowser(this.platformId)) {
      this.titleService.setTitle(p.title + this.envService.config.pageTitleSuffix)
      return
    }

    const productImage = p.getListPicture(this.selectedVariant())
    if(productImage) {
      this.seoService.updateImageSlug(productImage.slug, productImage.alt, 750)
    }
    this.seoService.updateTitle(p.title + this.envService.config.pageTitleSuffix)
    this.seoService.updateDescription(p.seoDescription ? p.seoDescription : p.shortDescription)
    this.seoService.updateType("website")
    this.seoService.updateOgUrl()
    this.seoService.updateDefaults()
  }

  private onProductPageLoad(productPage: ProductPageInterface) {
    const p = productPage.product
    const isSameProduct = this.loadedProductIdentity !== undefined
      && this.loadedProductIdentity === p.productId

    // Always refresh the page wrapper so Aktion prices/badges (applied via
    // shop-event re-emit) are reflected in the UI.
    this.productPageContent.set({ ... productPage })

    // On a shop-event re-emit of the *same* product we must not wipe the
    // already-resolved distinct product, reset the selected variant or
    // re-trigger the configurator preselection. Doing so would blank the order
    // tiles / add-to-cart button for Aktion products opened via the overview.
    // @see gea-waldviertler-website/angular-webshop – Aktion + overview defect
    if (isSameProduct) {
      // Re-emit the already-resolved distinct product with the refreshed Aktion
      // data. Scheduled via a macrotask so the child's subscription and the
      // current change-detection pass have settled (avoids
      // ExpressionChangedAfterItHasBeenChecked).
      setTimeout(() => this.distinctRefreshSubject.next(), 0)
      return
    }

    this.loadedProductIdentity = p.productId

    this.forcePrice.set(undefined)
    this.onDistinctProductChange(undefined)

    this.selectedVariant.set(0)

    // update all meta tags
    this.updateMetaTags(p)

    this.configuratorPreselection = getUrlHashArguments()
    let variantPictures
    // TODO: Fixme
    // hardcoded for schuhspende
    if (p.productId == this.ID_SCHUHSPENDE) {
      variantPictures = p.getPictures(this.ID_SCHUHSPENDE_VARIABEL)
    } else {
      variantPictures = p.getPictures(p.getVariant().variantId)
    }

    this.pictures.set(variantPictures)

    // apply linked Product Stock
    if(p.linkedProducts?.products.productList && p.linkedProducts.stock) {
      for(const linkedProduct of p.linkedProducts.products.productList) {
        for(const stock of p.linkedProducts.stock) {
          if(stock.productId == linkedProduct.productId) {
            linkedProduct.applyStock(stock)
          }
        }
      }
    }

    // Add button to breadcrumbs if product has a cmsPageId given
    if (p.cmsPageId !== 0) {
      this.bcService.setButtons({
        text: "Kaufen",
        type: "button",
        class: "btn-primary",
        onClick: () => {
          scrollToFirstEl('.product-container')
        }
      })
    } else {
      this.bcService.setButtons(undefined)
    }

    setTimeout(() => {
      scrollToFirstEl('.root-container', 0)
      this.pageloadService.getStatusSubject().next(false)
    }, 50)
  }

  onActiveVariantChange(variant: ProductVariantInterface | undefined) {
    if (! variant) return
    const pp = this.productPageContent()
    if (! pp || ! pp.product) return
    this.selectedVariant.set(variant.variantId)
    this.pictures.set(pp.product.getPictures(this.selectedVariant()))
    if (this.isBrowser()) {
      setTimeout(() => {
          scrollToFirstEl('.product-container')
      }, 50)
    }
  }

  onDistinctProductChange(distinctProduct?: DistinctProductInterfaceType | undefined) {
    this.distinctProduct = distinctProduct
    if (distinctProduct) {
      this.forcePrice.set(undefined)
    }
    this.distinctProductSignal.set(distinctProduct)
  }

  onSelectionChange(selections: AttributeSelectionsInterface) {
    this.currentSelections.set({ ... selections })
  }

  onAddToCart(data: AddToCartDataInterface) {
    this.cart.add(data)
    this.distinctProductSignal.update(value => undefined)
    // @see: gea-waldviertler-website/angular-webshop#675
    // this.resetSelectionSubject.next(true)
  }

  triggerValidation() {
    this.triggerValidationSubject.next(true)
  }

  // used for updating the price while configuring the product
  updatePrice(price: number) {
    if (! this.distinctProductSignal()) {
      this.forcePrice.set({ normalFrom: price } as PriceViewInterface)
    }
  }

  updatePicture(pictures?: PictureInterface[]) {
    const p = this.productPageContent()

    if (! p || ! (p.product instanceof ConfigurableProduct)) {
      return
    }

    if (! pictures) {
      return
    }

    let pics : PictureInterface[] = []
    for (let ps of pictures) {
      if (pics.length >= 4) break // hotfix for too many images

      if (! pics.find(x => (x.slug == ps.slug))) {
        pics.push(ps)
      }
    }

    this.pictures.set(pics)
  }

  getShareData() {
    const p = this.productPageContent()
    if (! p) return undefined
    let hash = ''
    const cs = this.currentSelections()
    if (cs) {
      hash += '#'
      // set url hash
      Object.values(cs).forEach((attribute, i: number) => {
        hash += (i > 0 ? '&' : '') + attribute.attributeCode + '=' + attribute.attributeValues.join(',')
      })
    }

    return {
      title: p.product?.title ?? '',
      text: p.product?.shortDescription ?? '',
      url: this.isBrowser() ? (window.location.origin + window.location.pathname + hash) : ''
    }
  }

  asConfigurableProduct(product: Product<ProductVariantInterface>): ConfigurableProduct {
    return product as ConfigurableProduct
  }

  asSimpleProduct(product: Product<ProductVariantInterface>): SimpleProduct {
    return product as SimpleProduct
  }

  asMatrixProduct(product: Product<ProductVariantInterface>): MatrixProduct {
    return product as MatrixProduct
  }
}
